// #region Imports
import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchBoardingSla,
    patchBoardingOverride,
    resetBoardingSla,
    resetPatchBoardingOverrideStatus,
    selectBoardingSlaData,
    selectBoardingSlaStatus,
    selectBoardingSlaError,
    selectPatchBoardingOverrideStatus,
} from 'redux/slices/sla-reports/boarding-sla';
import { setToast, setShowToast } from '_redux/slices/timeline/timeline-toast';
import BoardingSlaSummary from './boarding-sla-summary';
import BoardingSlaRawDataTable from './boarding-sla-raw-data-table';
import ReportHeader from './report-header';
// #endregion

// #region BoardingSlaReport Component
/**
 * Boarding SLA Report - Displays SLA boarding metrics for the selected date range and IATA.
 * @param {Object} props
 * @param {Array} props.dateRange - [startDate, endDate] for the report
 * @param {string} props.iata - IATA code to filter by
 */
const BoardingSlaReport = ({ dateRange, iata, runReportRef, onRunLoadingChange, hideBreakdown }) => {

    // #region State
    const [overrideContext, setOverrideContext] = useState(null);
    const [includeNoData, setIncludeNoData] = useState(false);
    // #endregion

    // #region Redux State
    const dispatch = useDispatch();
    const data = useSelector(selectBoardingSlaData);
    const status = useSelector(selectBoardingSlaStatus);
    const error = useSelector(selectBoardingSlaError);
    const patchStatus = useSelector(selectPatchBoardingOverrideStatus);
    const isLoading = status === 'loading';
    const isPatching = patchStatus === 'loading';
    // #endregion

    // #region handlers
    
    /** Fetches boarding SLA report for the selected date range and IATA */
    const handleRunReport = useCallback(() => {
        const startDate = dateRange?.[0];
        const endDate = dateRange?.[1];

        if (!startDate || !endDate || !iata) return;

        const toLocalDateString = (d) => { const dt = new Date(d); return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`; };
        const formattedStartDate = toLocalDateString(startDate);
        const formattedEndDate = toLocalDateString(endDate);

        dispatch(
            fetchBoardingSla({
                startDate: formattedStartDate,
                endDate: formattedEndDate,
                iata,
                hideBreakdown,
            })
        );
    }, [dispatch, dateRange, iata, hideBreakdown]);
    // #endregion

    // #region Effects
    /** Reset boarding SLA state on unmount */
    useEffect(() => {
        return () => {
            dispatch(resetBoardingSla());
        };
    }, [dispatch]);

    /** On patch success: toast, close modal, refetch report */
    useEffect(() => {
        if (patchStatus === 'succeeded') {
            dispatch(setToast({ message: 'Booking updated successfully', statusType: 'success' }));
            dispatch(setShowToast(true));
            dispatch(resetPatchBoardingOverrideStatus());
            setOverrideContext(null);
            const toLocalDateString = (d) => { const dt = new Date(d); return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`; };
            const startDate = dateRange?.[0] ? toLocalDateString(dateRange[0]) : null;
            const endDate = dateRange?.[1] ? toLocalDateString(dateRange[1]) : null;
            if (startDate && endDate && iata) {
                dispatch(fetchBoardingSla({ startDate, endDate, iata, hideBreakdown }));
            }
        }
    }, [patchStatus, dispatch, dateRange, iata, hideBreakdown]);

    /** On patch failure: toast error */
    useEffect(() => {
        if (patchStatus === 'failed') {
            dispatch(setToast({ message: 'Failed to update booking', statusType: 'failed' }));
            dispatch(setShowToast(true));
            dispatch(resetPatchBoardingOverrideStatus());
        }
    }, [patchStatus, dispatch]);

    useEffect(() => {
        if (runReportRef) runReportRef.current = handleRunReport;
        return () => { if (runReportRef) runReportRef.current = null; };
    }, [handleRunReport, runReportRef]);

    useEffect(() => {
        if (onRunLoadingChange) onRunLoadingChange(isLoading);
    }, [isLoading, onRunLoadingChange]);
    // #endregion

    // #region Derived Data
    const dailyReports = data?.dailyReports ?? [];
    const isMultiDay = (data?.dateRange?.length ?? 0) > 1;
    // #endregion

    // #region Render
    return (
        <div>

            <ReportHeader
                onRunReport={handleRunReport}
                isLoading={isLoading}
                hideRunButton={!!runReportRef}
            />

            {/* Error message when fetch fails */}
            {status === 'failed' && (
                <p style={{ color: '#991b1b', fontSize: '13px' }}>Error loading Boarding SLA report: {error}</p>
            )}

            {/* Summary and raw data when report has loaded */}
            {data && (
                <>
                    <BoardingSlaSummary
                        dailyReports={dailyReports}
                        overallSummary={isMultiDay ? data.summary : null}
                        includeNoData={includeNoData}
                    />
                    {!hideBreakdown && (
                        <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '24px', marginBottom: '12px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#4b5563', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={includeNoData}
                                        onChange={(e) => setIncludeNoData(e.target.checked)}
                                        style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#3b82f6' }}
                                    />
                                    Include no-data (skip) rows
                                </label>
                            </div>
                            <BoardingSlaRawDataTable
                                dailyReports={dailyReports}
                                overrideContext={overrideContext}
                                onOverrideRowChange={setOverrideContext}
                                onPatchBoarding={(row, pass, passReason, { ignore } = {}) => {
                                    if (row?.bookingId && overrideContext?.kpiKey) {
                                        dispatch(patchBoardingOverride({
                                            bookingId: row.bookingId,
                                            kpiKey: overrideContext.kpiKey,
                                            pass,
                                            passReason,
                                            ignore: !!ignore,
                                        }));
                                    }
                                }}
                                isPatching={isPatching}
                                includeNoData={includeNoData}
                            />
                        </>
                    )}
                </>
            )}

        </div>
    );
    // #endregion
};
// #endregion

export default BoardingSlaReport;
